import { useState, useEffect, useCallback } from 'react';
import {
  Plus, Search, Pencil, Trash2, Loader2, Users, ChevronLeft, ChevronRight,
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/components/ui/use-toast';
import api from '@/services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';

const TRY = (n) =>
  new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 2 }).format(n);

const EMPTY_FORM = { name: '', phone: '', email: '', opening_balance: '', notes: '' };

function useDebounce(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

export default function CariManagement() {
  const { hasPermission } = useAuth();
  const { toast } = useToast();

  const [caris, setCaris] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [isSaving, setIsSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const debouncedSearch = useDebounce(search, 300);

  const fetchCaris = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = { page, limit: 15 };
      if (debouncedSearch) params.search = debouncedSearch;
      const { data } = await api.get('/cari', { params });
      setCaris(data.data);
      setTotal(data.total);
      setTotalPages(data.totalPages);
    } catch {
      toast({ title: 'Hata', description: 'Cari listesi yüklenemedi.', variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  }, [page, debouncedSearch]); // eslint-disable-line

  useEffect(() => { fetchCaris(); }, [fetchCaris]);
  useEffect(() => { setPage(1); }, [debouncedSearch]);

  const openAdd = () => { setEditTarget(null); setForm(EMPTY_FORM); setDialogOpen(true); };
  const openEdit = (c) => {
    setEditTarget(c);
    setForm({ name: c.name, phone: c.phone || '', email: c.email || '', opening_balance: c.opening_balance, notes: c.notes || '' });
    setDialogOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      if (editTarget) {
        await api.put(`/cari/${editTarget.id}`, form);
        toast({ title: 'Güncellendi', description: `${form.name} başarıyla güncellendi.`, variant: 'success' });
      } else {
        await api.post('/cari', form);
        toast({ title: 'Eklendi', description: `${form.name} başarıyla eklendi.`, variant: 'success' });
      }
      setDialogOpen(false);
      fetchCaris();
    } catch (err) {
      toast({ title: 'Hata', description: err.response?.data?.error || 'İşlem başarısız.', variant: 'destructive' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await api.delete(`/cari/${deleteTarget.id}`);
      toast({ title: 'Silindi', description: `${deleteTarget.name} silindi.`, variant: 'success' });
      setDeleteTarget(null);
      fetchCaris();
    } catch (err) {
      toast({ title: 'Hata', description: err.response?.data?.error || 'Silme başarısız.', variant: 'destructive' });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="space-y-5">
      {/* Başlık */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2">
            Cari Yönetimi
            <Badge variant="secondary" className="text-xs font-normal">{total} kayıt</Badge>
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">Müşteri ve tedarikçi hesaplarını yönetin</p>
        </div>
        {hasPermission('create_cari') && (
          <Button onClick={openAdd} className="gap-2">
            <Plus className="w-4 h-4" /> Yeni Cari Ekle
          </Button>
        )}
      </div>

      {/* Arama */}
      <Card>
        <CardContent className="p-4">
          <div className="relative max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <Input
              placeholder="Ad, telefon veya email ara..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
        </CardContent>
      </Card>

      {/* Tablo */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center h-40">
              <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
            </div>
          ) : caris.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-40 text-slate-500">
              <Users className="w-10 h-10 mb-2 opacity-30" />
              <p className="text-sm">Kayıt bulunamadı</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Cari Adı</TableHead>
                  <TableHead>Telefon</TableHead>
                  <TableHead>E-posta</TableHead>
                  <TableHead className="text-right">Açılış Bakiyesi</TableHead>
                  <TableHead className="text-right">Güncel Bakiye</TableHead>
                  <TableHead className="text-right">İşlem</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {caris.map((c) => {
                  const bal = parseFloat(c.current_balance);
                  return (
                    <TableRow key={c.id}>
                      <TableCell className="font-medium text-slate-900 dark:text-slate-100">{c.name}</TableCell>
                      <TableCell className="text-slate-500 dark:text-slate-400">{c.phone || '—'}</TableCell>
                      <TableCell className="text-slate-500 dark:text-slate-400 text-xs">{c.email || '—'}</TableCell>
                      <TableCell className="text-right font-mono text-slate-500 dark:text-slate-400 text-xs">
                        {TRY(c.opening_balance)}
                      </TableCell>
                      <TableCell className={`text-right font-mono font-semibold ${bal >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                        {TRY(bal)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {hasPermission('edit_cari') && (
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:text-blue-400"
                              onClick={() => openEdit(c)}>
                              <Pencil className="w-3.5 h-3.5" />
                            </Button>
                          )}
                          {hasPermission('delete_cari') && (
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:text-rose-400"
                              onClick={() => setDeleteTarget(c)}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>

        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 dark:border-slate-800 text-sm text-slate-500 dark:text-slate-400">
            <span>Toplam {total} kayıt</span>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="icon" className="h-7 w-7" disabled={page === 1} onClick={() => setPage(p => p - 1)}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-slate-600 dark:text-slate-400">{page} / {totalPages}</span>
              <Button variant="ghost" size="icon" className="h-7 w-7" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Ekle/Düzenle Modalı */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editTarget ? 'Cari Düzenle' : 'Yeni Cari Ekle'}</DialogTitle>
            <DialogDescription>
              {editTarget ? `${editTarget.name} bilgilerini güncelleyin` : 'Yeni bir cari hesap oluşturun'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 space-y-1.5">
                <Label>Cari Adı *</Label>
                <Input placeholder="Firma veya kişi adı" value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required />
              </div>
              {!editTarget && (
                <div className="col-span-2 space-y-1.5">
                  <Label>Açılış Bakiyesi (₺)</Label>
                  <Input type="number" step="0.01" placeholder="0.00" value={form.opening_balance}
                    onChange={e => setForm(f => ({ ...f, opening_balance: e.target.value }))} />
                </div>
              )}
              <div className="space-y-1.5">
                <Label>Telefon</Label>
                <Input placeholder="0555 000 00 00" value={form.phone}
                  onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} />
              </div>
              <div className="space-y-1.5">
                <Label>E-posta</Label>
                <Input type="email" placeholder="ornek@firma.com" value={form.email}
                  onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
              </div>
              <div className="col-span-2 space-y-1.5">
                <Label>Notlar</Label>
                <Input placeholder="İsteğe bağlı not..." value={form.notes}
                  onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
              </div>
            </div>
            <DialogFooter className="gap-2 mt-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>İptal</Button>
              <Button type="submit" disabled={isSaving || !form.name}>
                {isSaving ? <><Loader2 className="w-4 h-4 animate-spin" /> Kaydediliyor...</> : 'Kaydet'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Silme Onay Modalı */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-rose-400">Cari Sil</DialogTitle>
            <DialogDescription>
              <strong className="text-slate-800 dark:text-slate-200">{deleteTarget?.name}</strong> ve tüm işlemleri kalıcı olarak silinecek. Bu işlem geri alınamaz.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 mt-2">
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>İptal</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={isDeleting}>
              {isDeleting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Sil'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
